import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Assignment as AssignmentIcon, Delete as DeleteIcon, KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import {
  assignMaterialEquipment,
  assignTool,
  createMachineFile,
  createMachineTemplateCategory,
  createMaterialEquipment,
  createMaterialEquipmentTemplate,
  createTool,
  createToolTemplate,
  deleteMachineFile,
  deleteMachineTemplateCategory,
  deleteMaterialEquipmentTemplate,
  deleteTool,
  deleteToolTemplate,
  getMachineFiles,
  getMachineFileUploadUrl,
  getMaterialEquipment,
  getMaterialEquipmentAssignments,
  getMaterialEquipmentHistory,
  getMaterialEquipmentTemplates,
  getPersonnel,
  getToolAssignments,
  getTools,
  getToolTemplates,
  unassignMaterialEquipment,
  unassignTool,
  updateMachineFile,
  updateMachineTemplateCategory,
  updateMaterialEquipment,
  updateMaterialEquipmentTemplate,
  updateTool,
  updateToolTemplate,
} from '../services/api';
import WarehouseInboundDialog from './warehouse/WarehouseInboundDialog';
import { UNITS } from '../constants/units';
import DateInputWithArrows from '../components/DateInputWithArrows';
import UpdateFileDueDateDialog from '../components/UpdateFileDueDateDialog';
import UpdateFileNotesDialog from '../components/UpdateFileNotesDialog';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatPersonnelName } from '../utils/personnelDisplay';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { canAccess } from '../utils/permissions';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`warehouse-tabpanel-${index}`} aria-labelledby={`warehouse-tab-${index}`}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function isAssignmentActive(assignment) {
  const now = new Date();
  const startDate = new Date(assignment.startDate);
  const endDate = assignment.endDate ? new Date(assignment.endDate) : null;
  return startDate <= now && (!endDate || endDate >= now);
}

function isAssignmentScheduled(assignment) {
  return new Date(assignment.startDate) > new Date();
}

function getAssignmentStatus(assignment) {
  if (isAssignmentActive(assignment)) return { label: 'Active today', color: 'success' };
  if (isAssignmentScheduled(assignment)) return { label: 'Scheduled', color: 'info' };
  return { label: 'Completed', color: 'default' };
}

function getToolTypeLabel(type, t) {
  const typeMap = {
    HAND_TOOL: t('tools.type.handTool', { defaultValue: 'Hand Tool' }),
    ELECTRIC_TOOL: t('tools.type.electricTool', { defaultValue: 'Electric Tool' }),
    MEASURING_EQUIPMENT: t('tools.type.measuringEquipment', { defaultValue: 'Measuring Equipment' }),
    CONSTRUCTION_TOOL: t('tools.type.constructionTool', { defaultValue: 'Construction Tool' }),
    AUXILIARY_TOOL: t('tools.type.auxiliaryTool', { defaultValue: 'Auxiliary Tool' }),
    OTHER: t('tools.type.other', { defaultValue: 'Other' }),
  };
  return typeMap[type] || type;
}

const TEMPLATE_FIELD_TYPES = ['TEXT', 'DATE', 'NUMBER', 'SELECT'];

function getTemplateFieldTypeLabel(fieldType, t) {
  const typeMap = {
    TEXT: t('warehouse.fieldTypes.text', { defaultValue: 'Text' }),
    DATE: t('warehouse.fieldTypes.date', { defaultValue: 'Date' }),
    NUMBER: t('warehouse.fieldTypes.number', { defaultValue: 'Number' }),
    SELECT: t('warehouse.fieldTypes.select', { defaultValue: 'Dropdown' }),
  };
  return typeMap[fieldType] || fieldType;
}

function getTemplateFieldOptions(field) {
  return Array.isArray(field?.options) ? field.options.filter((option) => String(option || '').trim()) : [];
}

function createBlankTemplateField() {
  return {
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    fieldType: 'TEXT',
    required: false,
    isDueDate: false,
    optionsText: '',
  };
}

function mapTemplateFieldsToForm(fields) {
  return (fields || []).map((field) => ({
    key: `field-${field.id}`,
    id: field.id,
    name: field.name || '',
    fieldType: field.fieldType || 'TEXT',
    required: Boolean(field.required),
    isDueDate: Boolean(field.isDueDate),
    optionsText: getTemplateFieldOptions(field).join('\n'),
  }));
}

function mapFormFieldsToPayload(fields) {
  return (fields || [])
    .filter((field) => String(field.name || '').trim())
    .map((field, index) => ({
      ...(typeof field.id === 'number' ? { id: field.id } : {}),
      name: String(field.name).trim(),
      fieldType: field.fieldType || 'TEXT',
      required: Boolean(field.required),
      isDueDate: field.fieldType === 'DATE' && Boolean(field.isDueDate),
      options: field.fieldType === 'SELECT'
        ? String(field.optionsText || '').split(/\r?\n/).map((option) => option.trim()).filter(Boolean)
        : undefined,
      sortOrder: index,
    }));
}

function fieldValuesFromMachine(machine) {
  const values = {};
  (machine?.fieldValues || []).forEach((entry) => {
    values[entry.fieldId] = entry.value || '';
  });
  return values;
}

function getFieldValueFromForm(fieldValues, fieldId) {
  if (!fieldValues) return '';
  if (fieldValues[fieldId] != null) return fieldValues[fieldId];
  if (fieldValues[String(fieldId)] != null) return fieldValues[String(fieldId)];
  return '';
}

function hasMissingRequiredFieldValues(fields, fieldValues) {
  return (fields || []).some((field) => field.required && !String(getFieldValueFromForm(fieldValues, field.id)).trim());
}

function getMachineFieldValue(machine, fieldId) {
  const match = (machine?.fieldValues || []).find((entry) => entry.fieldId === fieldId);
  return match?.value ?? '';
}

function formatTemplateFieldValue(field, value) {
  if (value == null || String(value).trim() === '') return '-';
  if (field.fieldType === 'DATE') {
    const datePart = String(value).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return formatDateEuropean(`${datePart}T12:00:00`) || '-';
    }
    return formatDateEuropean(value) || '-';
  }
  return String(value);
}

function Materials() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();

  const canMaterialsRead = canAccess(user, 'materials.read');
  const canMaterialsWrite = canAccess(user, 'materials.write');
  const canWarehouseRead = canAccess(user, 'warehouse.read');
  const canWarehouseWrite = canAccess(user, 'warehouse.write');
  const canToolsRead = canAccess(user, 'tools.read');
  const canToolsWrite = canAccess(user, 'tools.write');

  const [pageTab, setPageTab] = useState(0);
  const [inventorySubTab, setInventorySubTab] = useState(0);
  const [templateSubTab, setTemplateSubTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  const [inboundOpen, setInboundOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: '', unit: '', quantity: '', notes: '', templateId: '', fieldValues: {} });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyItem, setHistoryItem] = useState(null);

  const [machineTemplates, setMachineTemplates] = useState([]);
  const [toolTemplates, setToolTemplates] = useState([]);
  const [personnel, setPersonnel] = useState([]);

  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentRows, setAssignmentRows] = useState([]);
  const [assignmentMachine, setAssignmentMachine] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({ personnelId: '', startDate: '', endDate: '', notes: '' });

  const [filesOpen, setFilesOpen] = useState(false);
  const [filesMachine, setFilesMachine] = useState(null);
  const [machineFiles, setMachineFiles] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [renamingCategoryId, setRenamingCategoryId] = useState(null);
  const [renameCategoryName, setRenameCategoryName] = useState('');
  const [expirationByCategory, setExpirationByCategory] = useState({});
  const [notesByCategory, setNotesByCategory] = useState({});
  const [dueDateDialog, setDueDateDialog] = useState({ open: false, file: null });
  const [savingDueDate, setSavingDueDate] = useState(false);
  const [notesDialog, setNotesDialog] = useState({ open: false, file: null });
  const [savingNotes, setSavingNotes] = useState(false);

  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [toolFormData, setToolFormData] = useState({
    name: '',
    serialNumber: '',
    type: 'OTHER',
    dateReceived: '',
    dateReturned: '',
    notes: '',
    templateId: '',
  });

  const [toolAssignmentOpen, setToolAssignmentOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolAssignments, setToolAssignments] = useState([]);
  const [toolAssignmentData, setToolAssignmentData] = useState({
    personnelId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    notes: '',
  });

  const [machineTemplateDialogOpen, setMachineTemplateDialogOpen] = useState(false);
  const [editingMachineTemplate, setEditingMachineTemplate] = useState(null);
  const [machineTemplateForm, setMachineTemplateForm] = useState({ name: '', notes: '', fields: [] });

  const [toolTemplateDialogOpen, setToolTemplateDialogOpen] = useState(false);
  const [editingToolTemplate, setEditingToolTemplate] = useState(null);
  const [toolTemplateForm, setToolTemplateForm] = useState({ name: '', type: 'OTHER', notes: '' });

  const unitOptions = useMemo(() => Object.values(UNITS), []);

  const materialRows = useMemo(() => rows.filter((r) => r.type === 'Material'), [rows]);
  const machineRows = useMemo(() => rows.filter((r) => r.type === 'Machine'), [rows]);
  const equipmentRows = useMemo(() => rows.filter((r) => r.type === 'Equipment'), [rows]);
  const groupedMachineTables = useMemo(() => {
    const groupsById = new Map();
    const unassigned = [];
    machineRows.forEach((machine) => {
      if (!machine.templateId) {
        unassigned.push(machine);
        return;
      }
      if (!groupsById.has(machine.templateId)) {
        const template = machineTemplates.find((item) => item.id === machine.templateId)
          || machine.template
          || { id: machine.templateId, name: t('warehouse.noTemplate', { defaultValue: 'No template' }), fields: [] };
        groupsById.set(machine.templateId, { template, rows: [] });
      }
      groupsById.get(machine.templateId).rows.push(machine);
    });
    const groups = Array.from(groupsById.values()).sort((a, b) =>
      String(a.template.name || '').localeCompare(String(b.template.name || ''))
    );
    return { groups, unassigned };
  }, [machineRows, machineTemplates, t]);

  const inventoryTabs = useMemo(() => {
    const tabs = [];
    if (canMaterialsRead) {
      tabs.push({ key: 'materials', label: t('warehouse.sections.materials', { defaultValue: 'Materials' }) });
      tabs.push({ key: 'machines', label: t('warehouse.sections.machines', { defaultValue: 'Machines' }) });
      tabs.push({ key: 'equipment', label: t('warehouse.sections.equipment', { defaultValue: 'Equipment' }) });
    }
    if (canToolsRead) {
      tabs.push({ key: 'tools', label: t('warehouse.sections.tools', { defaultValue: 'Tools' }) });
    }
    return tabs;
  }, [canMaterialsRead, canToolsRead, t]);

  const activeInventoryTab = inventoryTabs[inventorySubTab]?.key;

  useEffect(() => {
    if (inventorySubTab >= inventoryTabs.length) {
      setInventorySubTab(0);
    }
  }, [inventorySubTab, inventoryTabs.length]);

  const load = async () => {
    setLoading(true);
    try {
      const requests = [getPersonnel()];
      if (canMaterialsRead) {
        requests.push(getMaterialEquipment({ sort: 'name', order: 'asc' }), getMaterialEquipmentTemplates());
      }
      if (canToolsRead) {
        requests.push(getTools(), getToolTemplates());
      }

      const results = await Promise.all(requests);
      let idx = 0;
      const personnelRes = results[idx++];
      setPersonnel(personnelRes.data || []);

      if (canMaterialsRead) {
        const materialsRes = results[idx++];
        const templatesRes = results[idx++];
        setRows(materialsRes.data || []);
        setMachineTemplates((templatesRes.data || []).filter((tpl) => tpl.type === 'Machine'));
      }

      if (canToolsRead) {
        const toolsRes = results[idx++];
        const toolTemplatesRes = results[idx++];
        setTools(toolsRes.data || []);
        setToolTemplates(toolTemplatesRes.data || []);
      }
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => { load(); }, [canMaterialsRead, canToolsRead]);

  useEffect(() => {
    if (!hasLoaded || loading) return;
    const openId = Number(searchParams.get('open'));
    const tab = searchParams.get('tab');
    const view = searchParams.get('view') || 'edit';

    if (tab === 'machines') {
      const machinesIndex = inventoryTabs.findIndex((item) => item.key === 'machines');
      if (machinesIndex >= 0 && (pageTab !== 0 || inventorySubTab !== machinesIndex)) {
        setPageTab(0);
        setInventorySubTab(machinesIndex);
      }
    }

    if (!openId) return;
    const machine = rows.find((row) => row.id === openId && row.type === 'Machine');
    if (!machine) return;

    if (view === 'files') {
      if (filesOpen && filesMachine?.id === openId) return;
      openFilesDialog(machine);
      return;
    }

    if (dialogOpen && editing?.id === openId) return;
    openEdit(machine);
  }, [
    hasLoaded,
    loading,
    rows,
    searchParams,
    inventoryTabs,
    pageTab,
    inventorySubTab,
    filesOpen,
    filesMachine,
    dialogOpen,
    editing,
  ]);

  const isMachine = form.type === 'Machine';
  const isMaterial = form.type === 'Material';
  const isEquipment = form.type === 'Equipment';

  const selectedTemplate = machineTemplates.find((template) => template.id === Number(form.templateId));
  const effectiveUnit = isMachine ? (selectedTemplate?.unit || UNITS.UNIT) : form.unit;

  const openEdit = (row) => {
    setEditing(row);
    setError(null);
    setForm({
      name: row.name || '',
      type: row.type || '',
      unit: row.type === 'Machine' ? UNITS.UNIT : (row.unit || ''),
      quantity: row.quantity ?? '',
      notes: row.notes || '',
      templateId: row.templateId || '',
      fieldValues: fieldValuesFromMachine(row),
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    if (searchParams.get('open')) {
      setSearchParams({}, { replace: true });
    }
  };
  const closeHistory = () => setHistoryOpen(false);
  const closeAssignment = () => setAssignmentOpen(false);
  const closeFiles = () => {
    setFilesOpen(false);
    if (searchParams.get('open')) {
      setSearchParams({}, { replace: true });
    }
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      type: form.type,
      unit: effectiveUnit,
      notes: form.notes || undefined,
    };

    if (isMachine) {
      payload.templateId = form.templateId ? Number(form.templateId) : null;
      payload.fieldValues = (selectedTemplate?.fields || []).map((field) => ({
        fieldId: field.id,
        value: getFieldValueFromForm(form.fieldValues, field.id),
      }));
    }

    if (!payload.name || !payload.type) return;
    if (!isMachine && !payload.unit) return;
    if (isMachine && hasMissingRequiredFieldValues(selectedTemplate?.fields, form.fieldValues)) {
      setError(t('warehouse.requiredFieldsMissing', { defaultValue: 'Fill in all required template fields' }));
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        await updateMaterialEquipment(editing.id, payload);
      } else {
        await createMaterialEquipment(payload);
      }
      closeDialog();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('common.saveFailed', { defaultValue: 'Error saving item' }));
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryItem(row);
    setHistoryOpen(true);
    const res = await getMaterialEquipmentHistory(row.id);
    setHistoryRows(res.data || []);
  };

  const loadAssignments = async (machineId) => {
    const res = await getMaterialEquipmentAssignments(machineId);
    setAssignmentRows(res.data || []);
  };

  const openAssignmentDialog = async (row) => {
    setAssignmentMachine(row);
    setAssignmentForm({ personnelId: '', startDate: '', endDate: '', notes: '' });
    setAssignmentOpen(true);
    await loadAssignments(row.id);
  };

  const handleAssignMachine = async () => {
    if (!assignmentMachine || !assignmentForm.personnelId || !assignmentForm.startDate) return;
    try {
      await assignMaterialEquipment(assignmentMachine.id, {
        personnelId: Number(assignmentForm.personnelId),
        startDate: assignmentForm.startDate,
        endDate: assignmentForm.endDate || undefined,
        notes: assignmentForm.notes || undefined,
      });
      setAssignmentForm({ personnelId: '', startDate: '', endDate: '', notes: '' });
      await Promise.all([loadAssignments(assignmentMachine.id), load()]);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleUnassignMachine = async (assignmentId) => {
    if (!assignmentMachine) return;
    try {
      await unassignMaterialEquipment(assignmentMachine.id, assignmentId);
      await Promise.all([loadAssignments(assignmentMachine.id), load()]);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const loadMachineFiles = async (machineId) => {
    const response = await getMachineFiles(machineId);
    setMachineFiles(response.data || []);
  };

  const openFilesDialog = async (row) => {
    setFilesMachine(row);
    setNewCategoryName('');
    setRenamingCategoryId(null);
    setRenameCategoryName('');
    setExpirationByCategory({});
    setFilesOpen(true);
    await loadMachineFiles(row.id);
  };

  const handleCreateCategory = async () => {
    if (!filesMachine?.templateId) return;
    const name = newCategoryName.trim();
    if (!name) return;
    await createMachineTemplateCategory(filesMachine.templateId, { name });
    setNewCategoryName('');
    await loadMachineFiles(filesMachine.id);
  };

  const handleRenameCategory = async (categoryId) => {
    const name = renameCategoryName.trim();
    if (!name || !filesMachine) return;
    await updateMachineTemplateCategory(categoryId, { name });
    setRenamingCategoryId(null);
    setRenameCategoryName('');
    await loadMachineFiles(filesMachine.id);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!filesMachine) return;
    await deleteMachineTemplateCategory(categoryId);
    await loadMachineFiles(filesMachine.id);
  };

  const handleUploadMachineFile = async (categoryId, file) => {
    if (!filesMachine || !file) return;
    const expirationDate = expirationByCategory[categoryId] || '';
    const notes = notesByCategory[categoryId] || '';
    const presignResponse = await getMachineFileUploadUrl(filesMachine.id, {
      categoryId,
      originalName: file.name,
      contentType: file.type || 'application/octet-stream',
    });
    const { uploadUrl, objectKey } = presignResponse.data;
    await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    await createMachineFile(filesMachine.id, {
      categoryId,
      objectKey,
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size || 0,
      expirationDate: expirationDate || undefined,
      notes: notes || undefined,
    });
    setExpirationByCategory((prev) => ({ ...prev, [categoryId]: '' }));
    setNotesByCategory((prev) => ({ ...prev, [categoryId]: '' }));
    await loadMachineFiles(filesMachine.id);
  };

  const handleDeleteMachineFile = async (fileId) => {
    if (!filesMachine) return;
    await deleteMachineFile(fileId);
    await loadMachineFiles(filesMachine.id);
  };

  const closeDueDateDialog = () => {
    if (savingDueDate) return;
    setDueDateDialog({ open: false, file: null });
  };

  const handleSaveDueDate = async (expirationDate) => {
    if (!dueDateDialog.file?.id) return;
    setSavingDueDate(true);
    try {
      await updateMachineFile(dueDateDialog.file.id, { expirationDate });
      setDueDateDialog({ open: false, file: null });
      if (filesMachine) await loadMachineFiles(filesMachine.id);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setSavingDueDate(false);
    }
  };

  const closeNotesDialog = () => {
    if (savingNotes) return;
    setNotesDialog({ open: false, file: null });
  };

  const handleSaveNotes = async (notes) => {
    if (!notesDialog.file?.id) return;
    setSavingNotes(true);
    try {
      await updateMachineFile(notesDialog.file.id, { notes });
      setNotesDialog({ open: false, file: null });
      if (filesMachine) await loadMachineFiles(filesMachine.id);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const getMovementLabel = (row) => {
    if (row.sourceType === 'SITE_DELIVERY') return t('materials.movement.siteDeliveryOut', { defaultValue: 'Site delivery (out)' });
    if (row.sourceType === 'SITE_DEPARTURE') return t('materials.movement.siteDepartureIn', { defaultValue: 'Site departure (in)' });
    if (row.sourceType === 'WRITE_OFF') return t('materials.movement.writeOffOut', { defaultValue: 'Write-off (out)' });
    if (row.sourceType === 'MERCHANT_DELIVERY') return t('materials.movement.merchantReceived', { defaultValue: 'Merchant received' });
    if (row.sourceType === 'MANUAL_ADD') return t('materials.movement.manualAdd', { defaultValue: 'Manual add' });
    if (row.sourceType === 'MANUAL_ADJUSTMENT') return t('materials.movement.manualCorrection', { defaultValue: 'Manual correction' });
    return row.sourceType;
  };

  const handleToolClose = () => {
    setToolDialogOpen(false);
    setEditingTool(null);
  };

  const handleToolEdit = (tool) => {
    setEditingTool(tool);
    setToolFormData({
      ...tool,
      dateReceived: tool.dateReceived.split('T')[0],
      dateReturned: tool.dateReturned ? tool.dateReturned.split('T')[0] : '',
      templateId: tool.templateId || '',
      type: tool.type || 'OTHER',
      notes: tool.notes || '',
    });
    setToolDialogOpen(true);
  };

  const handleToolDelete = async (id) => {
    if (window.confirm(t('tools.deleteToolConfirm', { defaultValue: 'Are you sure you want to delete this tool?' }))) {
      try {
        await deleteTool(id);
        await load();
      } catch (err) {
        console.error('Error deleting tool:', err);
      }
    }
  };

  const handleToolSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...toolFormData, templateId: toolFormData.templateId || null };
      if (editingTool) {
        await updateTool(editingTool.id, submitData);
      } else {
        await createTool(submitData);
      }
      handleToolClose();
      await load();
    } catch (err) {
      console.error('Error saving tool:', err);
    }
  };

  const handleToolChange = (e) => {
    setToolFormData({ ...toolFormData, [e.target.name]: e.target.value });
  };

  const handleToolTemplateChangeForTool = (e) => {
    const templateId = e.target.value;
    const selected = toolTemplates.find((tpl) => tpl.id.toString() === templateId);
    setToolFormData((prev) => ({
      ...prev,
      templateId,
      type: selected ? selected.type : prev.type,
    }));
  };

  const handleToolAssignmentOpen = async (tool) => {
    setSelectedTool(tool);
    setToolAssignmentOpen(true);
    const response = await getToolAssignments(tool.id);
    setToolAssignments(response.data || []);
  };

  const handleToolAssignmentClose = () => {
    setToolAssignmentOpen(false);
    setSelectedTool(null);
    setToolAssignmentData({
      personnelId: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      notes: '',
    });
  };

  const handleToolAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await assignTool(selectedTool.id, toolAssignmentData);
      const response = await getToolAssignments(selectedTool.id);
      setToolAssignments(response.data || []);
      setToolAssignmentData({
        personnelId: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        notes: '',
      });
    } catch (err) {
      console.error('Error assigning tool:', err);
    }
  };

  const handleToolUnassign = async (assignmentId) => {
    if (window.confirm(t('tools.removeAssignmentConfirm', { defaultValue: 'Are you sure you want to remove this assignment?' }))) {
      try {
        await unassignTool(selectedTool.id, assignmentId);
        const response = await getToolAssignments(selectedTool.id);
        setToolAssignments(response.data || []);
      } catch (err) {
        console.error('Error removing assignment:', err);
      }
    }
  };

  const handleToolAssignmentChange = (e) => {
    setToolAssignmentData({ ...toolAssignmentData, [e.target.name]: e.target.value });
  };

  const handleMachineTemplateOpen = () => {
    setMachineTemplateDialogOpen(true);
    setEditingMachineTemplate(null);
    setMachineTemplateForm({ name: '', notes: '', fields: [] });
    setError(null);
  };

  const handleMachineTemplateClose = () => {
    setMachineTemplateDialogOpen(false);
    setEditingMachineTemplate(null);
    setError(null);
  };

  const handleEditMachineTemplate = (template) => {
    setEditingMachineTemplate(template);
    setMachineTemplateForm({
      name: template.name,
      notes: template.notes || '',
      fields: mapTemplateFieldsToForm(template.fields),
    });
    setMachineTemplateDialogOpen(true);
  };

  const handleDeleteMachineTemplate = async (id) => {
    if (window.confirm(t('warehouse.deleteMachineTemplateConfirm', { defaultValue: 'Are you sure you want to delete this machine template?' }))) {
      try {
        await deleteMaterialEquipmentTemplate(id);
        await load();
      } catch (err) {
        setError(err.response?.data?.error || t('warehouse.errorDeletingMachineTemplate', { defaultValue: 'Error deleting machine template' }));
      }
    }
  };

  const handleMachineTemplateSubmit = async (e) => {
    e.preventDefault();
    const fields = mapFormFieldsToPayload(machineTemplateForm.fields);
    const missingSelectOptions = fields.some((field) => field.fieldType === 'SELECT' && (!field.options || field.options.length === 0));
    if (missingSelectOptions) {
      setError(t('warehouse.selectOptionsRequired', { defaultValue: 'Dropdown fields need at least one option' }));
      return;
    }
    try {
      const payload = {
        name: machineTemplateForm.name.trim(),
        type: 'Machine',
        unit: UNITS.UNIT,
        notes: machineTemplateForm.notes || undefined,
        fields,
      };
      if (editingMachineTemplate) {
        await updateMaterialEquipmentTemplate(editingMachineTemplate.id, payload);
      } else {
        await createMaterialEquipmentTemplate(payload);
      }
      handleMachineTemplateClose();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('warehouse.errorSavingMachineTemplate', { defaultValue: 'Error saving machine template' }));
    }
  };

  const updateMachineTemplateField = (index, patch) => {
    const fields = machineTemplateForm.fields.map((field, fieldIndex) => (
      fieldIndex === index ? { ...field, ...patch } : field
    ));
    setMachineTemplateForm({ ...machineTemplateForm, fields });
  };

  const addMachineTemplateField = () => {
    setMachineTemplateForm({
      ...machineTemplateForm,
      fields: [...machineTemplateForm.fields, createBlankTemplateField()],
    });
  };

  const removeMachineTemplateField = (index) => {
    setMachineTemplateForm({
      ...machineTemplateForm,
      fields: machineTemplateForm.fields.filter((_, fieldIndex) => fieldIndex !== index),
    });
  };

  const moveMachineTemplateField = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= machineTemplateForm.fields.length) return;
    const fields = [...machineTemplateForm.fields];
    [fields[index], fields[target]] = [fields[target], fields[index]];
    setMachineTemplateForm({ ...machineTemplateForm, fields });
  };

  const handleToolTemplateOpen = () => {
    setToolTemplateDialogOpen(true);
    setEditingToolTemplate(null);
    setToolTemplateForm({ name: '', type: 'OTHER', notes: '' });
    setError(null);
  };

  const handleToolTemplateClose = () => {
    setToolTemplateDialogOpen(false);
    setEditingToolTemplate(null);
    setError(null);
  };

  const handleEditToolTemplate = (template) => {
    setEditingToolTemplate(template);
    setToolTemplateForm({ name: template.name, type: template.type, notes: template.notes || '' });
    setToolTemplateDialogOpen(true);
  };

  const handleDeleteToolTemplate = async (id) => {
    if (window.confirm(t('tools.deleteTemplateConfirm', { defaultValue: 'Are you sure you want to delete this template?' }))) {
      try {
        await deleteToolTemplate(id);
        await load();
      } catch (err) {
        setError(err.response?.data?.error || t('tools.errorDeletingTemplate', { defaultValue: 'Error deleting template' }));
      }
    }
  };

  const handleToolTemplateSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingToolTemplate) {
        await updateToolTemplate(editingToolTemplate.id, toolTemplateForm);
      } else {
        await createToolTemplate(toolTemplateForm);
      }
      handleToolTemplateClose();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('tools.errorSavingTemplate', { defaultValue: 'Error saving template' }));
    }
  };

  const handleToolTemplateChange = (e) => {
    setToolTemplateForm({ ...toolTemplateForm, [e.target.name]: e.target.value });
  };

  const renderStockActions = (item) => (
    <>
      {canWarehouseRead && <Button size="small" onClick={() => openHistory(item)}>{t('common.details')}</Button>}
      {item.type === 'Machine' && canWarehouseRead && (
        <>
          <Button size="small" onClick={() => openAssignmentDialog(item)}>{t('tools.assign', { defaultValue: 'Assign' })}</Button>
          <Button size="small" onClick={() => openFilesDialog(item)}>{t('personnel.tabs.files')}</Button>
        </>
      )}
      {canMaterialsWrite && <Button size="small" onClick={() => openEdit(item)}>{t('common.edit')}</Button>}
    </>
  );

  const renderStockTable = (sectionRows, showUnit, showQuantity) => (
    isMobile ? (
      <ResponsiveDataList
        rows={sectionRows}
        getTitle={(item) => item.name}
        getSubtitle={(item) => (showUnit ? (item.unit || '-') : '')}
        renderFields={(item) => (
          <Stack spacing={0.5}>
            {showQuantity && (
              <Typography variant="body2">
                <b>{t('costSheetsPage.quantity', { defaultValue: 'Quantity' })}:</b> {item.quantity ?? '-'}
              </Typography>
            )}
            {item.notes && (
              <Typography variant="body2"><b>{t('personnel.fields.notes')}:</b> {item.notes}</Typography>
            )}
          </Stack>
        )}
        renderActions={renderStockActions}
      />
    ) : (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('projects.fields.name')}</TableCell>
              {showUnit && <TableCell>{t('costSheetsPage.unit', { defaultValue: 'Unit' })}</TableCell>}
              {showQuantity && <TableCell>{t('costSheetsPage.quantity', { defaultValue: 'Quantity' })}</TableCell>}
              <TableCell>{t('personnel.fields.notes')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sectionRows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.name}</TableCell>
                {showUnit && <TableCell>{r.unit}</TableCell>}
                {showQuantity && <TableCell>{r.quantity ?? ''}</TableCell>}
                <TableCell>{r.notes}</TableCell>
                <TableCell align="right">{renderStockActions(r)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  );

  const renderMachineSectionTable = (sectionRows, fields) => (
    isMobile ? (
      <ResponsiveDataList
        rows={sectionRows}
        getTitle={(item) => item.name}
        getSubtitle={(item) => item.notes || ''}
        renderFields={(item) => (
          <Stack spacing={0.5}>
            {(fields || []).map((field) => (
              <Typography key={field.id} variant="body2">
                <b>{field.name}:</b> {formatTemplateFieldValue(field, getMachineFieldValue(item, field.id))}
              </Typography>
            ))}
            {item.notes && (
              <Typography variant="body2"><b>{t('personnel.fields.notes')}:</b> {item.notes}</Typography>
            )}
          </Stack>
        )}
        renderActions={renderStockActions}
      />
    ) : (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('projects.fields.name')}</TableCell>
              {(fields || []).map((field) => (
                <TableCell key={field.id}>{field.name}</TableCell>
              ))}
              <TableCell>{t('personnel.fields.notes')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sectionRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.name}</TableCell>
                {(fields || []).map((field) => (
                  <TableCell key={field.id}>{formatTemplateFieldValue(field, getMachineFieldValue(row, field.id))}</TableCell>
                ))}
                <TableCell>{row.notes}</TableCell>
                <TableCell align="right">{renderStockActions(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  );

  const renderMachineTables = () => (
    <Stack spacing={3}>
      {groupedMachineTables.groups.map(({ template, rows: sectionRows }) => (
        <Box key={template.id}>
          <Typography variant="h6" sx={{ mb: 1 }}>{template.name}</Typography>
          {renderMachineSectionTable(sectionRows, template.fields || [])}
        </Box>
      ))}
      {groupedMachineTables.unassigned.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>{t('warehouse.noTemplate', { defaultValue: 'No template' })}</Typography>
          {renderMachineSectionTable(groupedMachineTables.unassigned, [])}
        </Box>
      )}
    </Stack>
  );

  const toolColumns = useMemo(() => [
    { field: 'name', headerName: t('projects.fields.name'), width: 200, flex: 1 },
    { field: 'serialNumber', headerName: t('tools.fields.serialNumber', { defaultValue: 'Serial Number' }), width: 150 },
    {
      field: 'type',
      headerName: t('tools.fields.type', { defaultValue: 'Type' }),
      width: 150,
      valueFormatter: (params) => getToolTypeLabel(params.value, t),
    },
    {
      field: 'dateReceived',
      headerName: t('tools.fields.dateReceived', { defaultValue: 'Date Received' }),
      width: 150,
      valueFormatter: (params) => formatDateEuropean(params.value),
    },
    {
      field: 'dateReturned',
      headerName: t('tools.fields.dateReturned', { defaultValue: 'Date Returned' }),
      width: 150,
      valueFormatter: (params) => (params.value ? formatDateEuropean(params.value) : '-'),
    },
    { field: 'notes', headerName: t('personnel.fields.notes'), width: 200, flex: 1 },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 280,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {canToolsWrite && <Button size="small" onClick={() => handleToolEdit(params.row)}>{t('common.edit')}</Button>}
          {canToolsWrite && <Button size="small" color="error" onClick={() => handleToolDelete(params.row.id)}>{t('common.delete')}</Button>}
          <Tooltip title={t('tools.manageAssignments', { defaultValue: 'Manage Assignments' })}>
            <IconButton size="small" onClick={() => handleToolAssignmentOpen(params.row)} color="primary">
              <AssignmentIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [t, canToolsWrite]);

  const renderToolsTable = () => (
    isMobile ? (
      <ResponsiveDataList
        rows={tools}
        getTitle={(tool) => tool.name}
        getSubtitle={(tool) => tool.serialNumber || '-'}
        renderFields={(tool) => (
          <Stack spacing={0.5}>
            <Typography variant="body2"><b>{t('tools.fields.type', { defaultValue: 'Type' })}:</b> {getToolTypeLabel(tool.type, t)}</Typography>
            <Typography variant="body2"><b>{t('tools.fields.received', { defaultValue: 'Received' })}:</b> {tool.dateReceived ? formatDateEuropean(tool.dateReceived) : '-'}</Typography>
          </Stack>
        )}
        renderActions={(tool) => (
          <>
            {canToolsWrite && <Button size="small" onClick={() => handleToolEdit(tool)}>{t('common.edit')}</Button>}
            {canToolsWrite && <Button size="small" color="error" onClick={() => handleToolDelete(tool.id)}>{t('common.delete')}</Button>}
            <Button size="small" onClick={() => handleToolAssignmentOpen(tool)}>{t('tools.assign', { defaultValue: 'Assign' })}</Button>
          </>
        )}
      />
    ) : (
      <DataGrid
        rows={tools}
        columns={toolColumns}
        pageSize={10}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
      />
    )
  );

  const machineTemplateColumns = useMemo(() => [
    { field: 'name', headerName: t('tools.fields.templateName', { defaultValue: 'Template Name' }), width: 250, flex: 1 },
    {
      field: 'fields',
      headerName: t('warehouse.customFields', { defaultValue: 'Custom fields' }),
      width: 140,
      valueGetter: (params) => (params.row.fields || []).length,
    },
    { field: 'notes', headerName: t('personnel.fields.notes'), width: 200, flex: 1 },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {canMaterialsWrite && <Button size="small" onClick={() => handleEditMachineTemplate(params.row)}>{t('common.edit')}</Button>}
          {canMaterialsWrite && <Button size="small" color="error" onClick={() => handleDeleteMachineTemplate(params.row.id)}>{t('common.delete')}</Button>}
        </Box>
      ),
    },
  ], [t, canMaterialsWrite]);

  const toolTemplateColumns = useMemo(() => [
    { field: 'name', headerName: t('tools.fields.templateName', { defaultValue: 'Template Name' }), width: 250, flex: 1 },
    {
      field: 'type',
      headerName: t('tools.fields.type', { defaultValue: 'Type' }),
      width: 150,
      valueFormatter: (params) => getToolTypeLabel(params.value, t),
    },
    { field: 'notes', headerName: t('personnel.fields.notes'), width: 200, flex: 1 },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {canToolsWrite && <Button size="small" onClick={() => handleEditToolTemplate(params.row)}>{t('common.edit')}</Button>}
          {canToolsWrite && <Button size="small" color="error" onClick={() => handleDeleteToolTemplate(params.row.id)}>{t('common.delete')}</Button>}
        </Box>
      ),
    },
  ], [t, canToolsWrite]);

  const getDialogTitle = () => {
    if (editing) return t('materials.editItem', { defaultValue: 'Edit item' });
    if (isMaterial) return t('warehouse.addMaterial', { defaultValue: 'Add material' });
    if (isMachine) return t('warehouse.addMachine', { defaultValue: 'Add machine' });
    if (isEquipment) return t('warehouse.addEquipment', { defaultValue: 'Add equipment' });
    return t('materials.addItem', { defaultValue: 'Add item' });
  };

  return (
    <Box>
      <Typography variant="h5" mb={2}>{t('nav.warehouse')}</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <WarehouseInboundDialog
        open={inboundOpen}
        onClose={() => setInboundOpen(false)}
        onSaved={() => { load(); }}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={pageTab} onChange={(_, v) => setPageTab(v)}>
          <Tab label={t('warehouse.tabs.inventory', { defaultValue: 'Inventory' })} />
          {(canMaterialsRead || canToolsRead) && (
            <Tab label={t('warehouse.tabs.templates', { defaultValue: 'Templates' })} />
          )}
        </Tabs>
      </Box>

      <TabPanel value={pageTab} index={0}>
        {inventoryTabs.length > 0 && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={inventorySubTab} onChange={(_, v) => setInventorySubTab(v)}>
                {inventoryTabs.map((tab) => (
                  <Tab key={tab.key} label={tab.label} />
                ))}
              </Tabs>
            </Box>

            {(canWarehouseWrite || canMaterialsWrite) && (
              <Stack direction="row" justifyContent="flex-end" spacing={1} mb={2}>
                <Button variant="outlined" onClick={() => setInboundOpen(true)}>{t('warehouse.inboundTitle')}</Button>
              </Stack>
            )}

            {activeInventoryTab === 'materials' && renderStockTable(materialRows, true, true)}
            {activeInventoryTab === 'machines' && renderMachineTables()}
            {activeInventoryTab === 'equipment' && renderStockTable(equipmentRows, true, true)}
            {activeInventoryTab === 'tools' && renderToolsTable()}
          </>
        )}
      </TabPanel>

      <TabPanel value={pageTab} index={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={templateSubTab} onChange={(_, v) => setTemplateSubTab(v)}>
            {canMaterialsRead && <Tab label={t('warehouse.templates.machineTemplates', { defaultValue: 'Machine Templates' })} />}
            {canToolsRead && <Tab label={t('tools.templates', { defaultValue: 'Tool Templates' })} />}
          </Tabs>
        </Box>

        {canMaterialsRead && templateSubTab === 0 && (
          <Box>
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              {canMaterialsWrite && (
                <Button variant="contained" onClick={handleMachineTemplateOpen}>
                  {t('warehouse.addMachineTemplate', { defaultValue: 'Add Machine Template' })}
                </Button>
              )}
            </Stack>
            {isMobile ? (
              <ResponsiveDataList
                rows={machineTemplates}
                getTitle={(template) => template.name}
                getSubtitle={(template) => t('warehouse.fieldCount', {
                  defaultValue: '{{count}} fields',
                  count: (template.fields || []).length,
                })}
                renderFields={(template) => (
                  <Typography variant="body2">{template.notes || t('tools.noNotes', { defaultValue: 'No notes' })}</Typography>
                )}
                renderActions={(template) => (
                  <>
                    {canMaterialsWrite && <Button size="small" onClick={() => handleEditMachineTemplate(template)}>{t('common.edit')}</Button>}
                    {canMaterialsWrite && <Button size="small" color="error" onClick={() => handleDeleteMachineTemplate(template.id)}>{t('common.delete')}</Button>}
                  </>
                )}
              />
            ) : (
              <DataGrid
                rows={machineTemplates}
                columns={machineTemplateColumns}
                pageSize={10}
                loading={loading}
                autoHeight
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
              />
            )}
          </Box>
        )}

        {canToolsRead && templateSubTab === (canMaterialsRead ? 1 : 0) && (
          <Box>
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              {canToolsWrite && (
                <Button variant="contained" onClick={handleToolTemplateOpen}>
                  {t('tools.addTemplate', { defaultValue: 'Add Template' })}
                </Button>
              )}
            </Stack>
            {isMobile ? (
              <ResponsiveDataList
                rows={toolTemplates}
                getTitle={(template) => template.name}
                getSubtitle={(template) => getToolTypeLabel(template.type, t)}
                renderFields={(template) => (
                  <Typography variant="body2">{template.notes || t('tools.noNotes', { defaultValue: 'No notes' })}</Typography>
                )}
                renderActions={(template) => (
                  <>
                    {canToolsWrite && <Button size="small" onClick={() => handleEditToolTemplate(template)}>{t('common.edit')}</Button>}
                    {canToolsWrite && <Button size="small" color="error" onClick={() => handleDeleteToolTemplate(template.id)}>{t('common.delete')}</Button>}
                  </>
                )}
              />
            ) : (
              <DataGrid
                rows={toolTemplates}
                columns={toolTemplateColumns}
                pageSize={10}
                loading={loading}
                autoHeight
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
              />
            )}
          </Box>
        )}
      </TabPanel>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth={isMachine ? 'md' : 'sm'} fullScreen={isMobile}>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label={t('projects.fields.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            {!isMachine && (
              <FormControl fullWidth>
                <InputLabel id="unit-label">{t('costSheetsPage.unit', { defaultValue: 'Unit' })}</InputLabel>
                <Select labelId="unit-label" label={t('costSheetsPage.unit', { defaultValue: 'Unit' })} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                  {unitOptions.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {isMachine && (
              <FormControl fullWidth>
                <InputLabel id="template-label">{t('warehouse.machineTemplate', { defaultValue: 'Machine Template' })}</InputLabel>
                <Select
                  labelId="template-label"
                  label={t('warehouse.machineTemplate', { defaultValue: 'Machine Template' })}
                  value={form.templateId}
                  onChange={(e) => setForm({ ...form, templateId: e.target.value, fieldValues: {} })}
                >
                  <MenuItem value="">{t('warehouse.noTemplate', { defaultValue: 'No template' })}</MenuItem>
                  {machineTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {isMachine && (selectedTemplate?.fields || []).map((field) => {
              const value = getFieldValueFromForm(form.fieldValues, field.id);
              const setValue = (nextValue) => setForm({
                ...form,
                fieldValues: { ...form.fieldValues, [field.id]: nextValue },
              });
              const label = field.required ? `${field.name} *` : field.name;
              if (field.fieldType === 'DATE') {
                return (
                  <DateInputWithArrows
                    key={field.id}
                    label={label}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    fullWidth
                    required={field.required}
                    margin="none"
                  />
                );
              }
              if (field.fieldType === 'NUMBER') {
                return (
                  <TextField
                    key={field.id}
                    label={label}
                    type="number"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    fullWidth
                    required={field.required}
                  />
                );
              }
              if (field.fieldType === 'SELECT') {
                const options = getTemplateFieldOptions(field);
                const selectOptions = value && !options.includes(value) ? [value, ...options] : options;
                return (
                  <FormControl key={field.id} fullWidth required={field.required}>
                    <InputLabel id={`machine-field-${field.id}`}>{label}</InputLabel>
                    <Select
                      labelId={`machine-field-${field.id}`}
                      label={label}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                    >
                      <MenuItem value="">{field.required ? '' : t('warehouse.optional', { defaultValue: 'Optional' })}</MenuItem>
                      {selectOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }
              return (
                <TextField
                  key={field.id}
                  label={label}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  fullWidth
                  required={field.required}
                />
              );
            })}
            <TextField label={t('personnel.fields.notes')} multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              loading
              || !form.name
              || !form.type
              || (!isMachine && !effectiveUnit)
              || (isMachine && hasMissingRequiredFieldValues(selectedTemplate?.fields, form.fieldValues))
            }
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={toolDialogOpen} onClose={handleToolClose} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle>{editingTool ? t('tools.editTool', { defaultValue: 'Edit Tool' }) : t('tools.addTool', { defaultValue: 'Add Tool' })}</DialogTitle>
        <form onSubmit={handleToolSubmit}>
          <DialogContent>
            <TextField fullWidth margin="normal" name="name" label={t('tools.fields.toolName', { defaultValue: 'Tool Name' })} value={toolFormData.name} onChange={handleToolChange} required />
            <TextField fullWidth margin="normal" name="serialNumber" label={t('tools.fields.serialNumber', { defaultValue: 'Serial Number' })} value={toolFormData.serialNumber} onChange={handleToolChange} required />
            <TextField
              select
              fullWidth
              margin="normal"
              name="templateId"
              label={t('tools.fields.toolTemplate', { defaultValue: 'Tool Template' })}
              value={toolFormData.templateId}
              onChange={handleToolTemplateChangeForTool}
              helperText={toolFormData.templateId ? t('tools.templateAutoType', { defaultValue: 'Tool type will be set automatically' }) : t('tools.templateOptional', { defaultValue: 'Optional - helps categorize your tool' })}
            >
              <MenuItem value="">{t('tools.selectTemplateOptional', { defaultValue: 'Select a template (optional)' })}</MenuItem>
              {toolTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name} ({template.type.replace('_', ' ').toLowerCase()})
                </MenuItem>
              ))}
            </TextField>
            {toolFormData.templateId && (
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200', mb: 2 }}>
                <Typography variant="body2" color="primary.main">
                  Template selected: {toolTemplates.find((tpl) => tpl.id.toString() === toolFormData.templateId)?.name}
                </Typography>
              </Box>
            )}
            <TextField select fullWidth margin="normal" name="type" label={t('tools.fields.toolType', { defaultValue: 'Tool Type' })} value={toolFormData.type} onChange={handleToolChange} required disabled={toolFormData.templateId}>
              <MenuItem value="HAND_TOOL">{t('tools.type.handTool', { defaultValue: 'Hand Tool' })}</MenuItem>
              <MenuItem value="ELECTRIC_TOOL">{t('tools.type.electricTool', { defaultValue: 'Electric Tool' })}</MenuItem>
              <MenuItem value="MEASURING_EQUIPMENT">{t('tools.type.measuringEquipment', { defaultValue: 'Measuring Equipment' })}</MenuItem>
              <MenuItem value="CONSTRUCTION_TOOL">{t('tools.type.constructionTool', { defaultValue: 'Construction Tool' })}</MenuItem>
              <MenuItem value="AUXILIARY_TOOL">{t('tools.type.auxiliaryTool', { defaultValue: 'Auxiliary Tool' })}</MenuItem>
              <MenuItem value="OTHER">{t('tools.type.other', { defaultValue: 'Other' })}</MenuItem>
            </TextField>
            <DateInputWithArrows fullWidth margin="normal" name="dateReceived" label={t('tools.fields.dateReceived', { defaultValue: 'Date Received' })} value={toolFormData.dateReceived} onChange={handleToolChange} required />
            <DateInputWithArrows fullWidth margin="normal" name="dateReturned" label={t('tools.fields.dateReturned', { defaultValue: 'Date Returned' })} value={toolFormData.dateReturned} onChange={handleToolChange} />
            <TextField fullWidth margin="normal" name="notes" label={t('tools.fields.notes', { defaultValue: 'Notes' })} multiline rows={4} value={toolFormData.notes} onChange={handleToolChange} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleToolClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={machineTemplateDialogOpen} onClose={handleMachineTemplateClose} fullScreen={isMobile} fullWidth maxWidth="md">
        <DialogTitle>
          {editingMachineTemplate
            ? t('warehouse.editMachineTemplate', { defaultValue: 'Edit Machine Template' })
            : t('warehouse.addMachineTemplate', { defaultValue: 'Add Machine Template' })}
        </DialogTitle>
        <form onSubmit={handleMachineTemplateSubmit}>
          <DialogContent>
            <TextField fullWidth margin="normal" name="name" label={t('tools.fields.templateName', { defaultValue: 'Template Name' })} value={machineTemplateForm.name} onChange={(e) => setMachineTemplateForm({ ...machineTemplateForm, name: e.target.value })} required />
            <TextField fullWidth margin="normal" name="notes" label={t('personnel.fields.notes')} multiline rows={4} value={machineTemplateForm.notes} onChange={(e) => setMachineTemplateForm({ ...machineTemplateForm, notes: e.target.value })} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1">{t('warehouse.customFields', { defaultValue: 'Custom fields' })}</Typography>
              <Button size="small" onClick={addMachineTemplateField}>{t('warehouse.addField', { defaultValue: 'Add field' })}</Button>
            </Stack>
            <Stack spacing={2}>
              {(machineTemplateForm.fields || []).map((field, index) => (
                <Box key={field.key || field.id || index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <TextField
                        fullWidth
                        label={t('warehouse.fieldName', { defaultValue: 'Field name' })}
                        value={field.name}
                        onChange={(e) => updateMachineTemplateField(index, { name: e.target.value })}
                      />
                      <TextField
                        select
                        sx={{ minWidth: 160 }}
                        label={t('warehouse.fieldType', { defaultValue: 'Field type' })}
                        value={field.fieldType}
                        onChange={(e) => {
                          const fieldType = e.target.value;
                          updateMachineTemplateField(index, {
                            fieldType,
                            isDueDate: fieldType === 'DATE' ? Boolean(field.isDueDate) : false,
                          });
                        }}
                      >
                        {TEMPLATE_FIELD_TYPES.map((fieldType) => (
                          <MenuItem key={fieldType} value={fieldType}>{getTemplateFieldTypeLabel(fieldType, t)}</MenuItem>
                        ))}
                      </TextField>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={Boolean(field.required)}
                            onChange={(e) => updateMachineTemplateField(index, { required: e.target.checked })}
                          />
                        )}
                        label={field.required
                          ? t('warehouse.required', { defaultValue: 'Required' })
                          : t('warehouse.optional', { defaultValue: 'Optional' })}
                        sx={{ minWidth: 140 }}
                      />
                      <Stack direction="row">
                        <IconButton
                          size="small"
                          onClick={() => moveMachineTemplateField(index, -1)}
                          disabled={index === 0}
                          aria-label={t('warehouse.moveFieldUp', { defaultValue: 'Move field up' })}
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveMachineTemplateField(index, 1)}
                          disabled={index === (machineTemplateForm.fields || []).length - 1}
                          aria-label={t('warehouse.moveFieldDown', { defaultValue: 'Move field down' })}
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeMachineTemplateField(index)}
                          aria-label={t('warehouse.deleteField', { defaultValue: 'Delete field' })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                    {field.fieldType === 'DATE' && (
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={Boolean(field.isDueDate)}
                            onChange={(e) => updateMachineTemplateField(index, { isDueDate: e.target.checked })}
                          />
                        )}
                        label={t('warehouse.dueDateExpires', { defaultValue: 'Due date (expires)' })}
                      />
                    )}
                    {field.fieldType === 'SELECT' && (
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label={t('warehouse.fieldOptions', { defaultValue: 'Options' })}
                        helperText={t('warehouse.fieldOptionsHint', { defaultValue: 'One option per line' })}
                        value={field.optionsText || ''}
                        onChange={(e) => updateMachineTemplateField(index, { optionsText: e.target.value })}
                      />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMachineTemplateClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={toolTemplateDialogOpen} onClose={handleToolTemplateClose} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle>{editingToolTemplate ? t('tools.editTemplate', { defaultValue: 'Edit Template' }) : t('tools.addTemplate', { defaultValue: 'Add Template' })}</DialogTitle>
        <form onSubmit={handleToolTemplateSubmit}>
          <DialogContent>
            <TextField fullWidth margin="normal" name="name" label={t('tools.fields.templateName', { defaultValue: 'Template Name' })} value={toolTemplateForm.name} onChange={handleToolTemplateChange} required />
            <TextField select fullWidth margin="normal" name="type" label={t('tools.fields.toolType', { defaultValue: 'Tool Type' })} value={toolTemplateForm.type} onChange={handleToolTemplateChange} required>
              <MenuItem value="HAND_TOOL">{t('tools.type.handTool', { defaultValue: 'Hand Tool' })}</MenuItem>
              <MenuItem value="ELECTRIC_TOOL">{t('tools.type.electricTool', { defaultValue: 'Electric Tool' })}</MenuItem>
              <MenuItem value="MEASURING_EQUIPMENT">{t('tools.type.measuringEquipment', { defaultValue: 'Measuring Equipment' })}</MenuItem>
              <MenuItem value="CONSTRUCTION_TOOL">{t('tools.type.constructionTool', { defaultValue: 'Construction Tool' })}</MenuItem>
              <MenuItem value="AUXILIARY_TOOL">{t('tools.type.auxiliaryTool', { defaultValue: 'Auxiliary Tool' })}</MenuItem>
              <MenuItem value="OTHER">{t('tools.type.other', { defaultValue: 'Other' })}</MenuItem>
            </TextField>
            <TextField fullWidth margin="normal" name="notes" label={t('tools.fields.notes', { defaultValue: 'Notes' })} multiline rows={4} value={toolTemplateForm.notes} onChange={handleToolTemplateChange} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleToolTemplateClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={assignmentOpen} onClose={closeAssignment} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle>{assignmentMachine ? `${t('tools.assignments', { defaultValue: 'Assignments' })} - ${assignmentMachine.name}` : t('tools.assignments', { defaultValue: 'Assignments' })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="machine-personnel-label">Personnel</InputLabel>
              <Select labelId="machine-personnel-label" label="Personnel" value={assignmentForm.personnelId} onChange={(e) => setAssignmentForm({ ...assignmentForm, personnelId: e.target.value })}>
                {personnel.map((person) => (
                  <MenuItem key={person.id} value={person.id}>{`${person.firstName} ${person.lastName}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <DateInputWithArrows label={t('personnel.fields.startDate')} value={assignmentForm.startDate} onChange={(e) => setAssignmentForm({ ...assignmentForm, startDate: e.target.value })} />
              <DateInputWithArrows label={t('personnel.fields.endDate')} value={assignmentForm.endDate} onChange={(e) => setAssignmentForm({ ...assignmentForm, endDate: e.target.value })} />
            </Stack>
            <TextField label={t('personnel.fields.notes')} value={assignmentForm.notes} onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })} />
            <Button variant="contained" onClick={handleAssignMachine} disabled={!assignmentForm.personnelId || !assignmentForm.startDate}>Assign machine</Button>
          </Stack>
          <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Personnel</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignmentRows.map((assignment) => (
                  <TableRow key={assignment.id} sx={isAssignmentActive(assignment) ? { bgcolor: 'success.light' } : undefined}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{`${assignment.personnel?.firstName || ''} ${assignment.personnel?.lastName || ''}`.trim()}</Typography>
                        <Chip size="small" label={getAssignmentStatus(assignment).label} color={getAssignmentStatus(assignment).color} variant={isAssignmentActive(assignment) ? 'filled' : 'outlined'} />
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDateEuropean(assignment.startDate)}</TableCell>
                    <TableCell>{assignment.endDate ? formatDateEuropean(assignment.endDate) : t('materials.openEnded', { defaultValue: 'Open-ended' })}</TableCell>
                    <TableCell>{assignment.notes || ''}</TableCell>
                    <TableCell align="right">
                      {(isAssignmentActive(assignment) || isAssignmentScheduled(assignment)) && (
                        <Button size="small" color="error" onClick={() => handleUnassignMachine(assignment.id)}>
                          {isAssignmentScheduled(assignment) ? 'Cancel' : 'Unassign'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignment}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={toolAssignmentOpen} onClose={handleToolAssignmentClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('tools.assignments', { defaultValue: 'Tool Assignments' })} - {selectedTool?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>{t('tools.currentAssignments', { defaultValue: 'Current Assignments' })}</Typography>
            <List>
              {toolAssignments.map((assignment) => (
                <ListItem
                  key={assignment.id}
                  secondaryAction={(
                    <IconButton edge="end" onClick={() => handleToolUnassign(assignment.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  )}
                >
                  <ListItemText
                    primary={formatPersonnelName(assignment.personnel)}
                    secondary={`${t('tools.from', { defaultValue: 'From' })}: ${formatDateEuropean(assignment.startDate)} ${assignment.endDate ? `${t('tools.to', { defaultValue: 'To' })}: ${formatDateEuropean(assignment.endDate)}` : t('tools.active', { defaultValue: '(Active)' })}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          <Typography variant="h6" gutterBottom>{t('tools.newAssignment', { defaultValue: 'New Assignment' })}</Typography>
          <form onSubmit={handleToolAssignmentSubmit}>
            <TextField fullWidth margin="normal" name="personnelId" label={t('tools.fields.assignTo', { defaultValue: 'Assign To' })} select value={toolAssignmentData.personnelId} onChange={handleToolAssignmentChange} required>
              {personnel.map((person) => (
                <MenuItem key={person.id} value={person.id}>{formatPersonnelName(person)} - {person.company?.name}</MenuItem>
              ))}
            </TextField>
            <DateInputWithArrows fullWidth margin="normal" name="startDate" label={t('personnel.fields.startDate')} value={toolAssignmentData.startDate} onChange={handleToolAssignmentChange} required />
            <DateInputWithArrows fullWidth margin="normal" name="endDate" label={t('personnel.fields.endDate')} value={toolAssignmentData.endDate} onChange={handleToolAssignmentChange} />
            <TextField fullWidth margin="normal" name="notes" label={t('tools.fields.notes', { defaultValue: 'Notes' })} multiline rows={2} value={toolAssignmentData.notes} onChange={handleToolAssignmentChange} />
            <Box sx={{ mt: 2 }}>
              <Button type="submit" variant="contained" color="primary">{t('tools.assignTool', { defaultValue: 'Assign Tool' })}</Button>
            </Box>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleToolAssignmentClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={filesOpen} onClose={closeFiles} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle>{filesMachine ? `${t('warehouse.machineFiles')} - ${filesMachine.name}` : t('warehouse.machineFiles')}</DialogTitle>
        <DialogContent>
          {!filesMachine?.templateId ? (
            <Typography color="text.secondary">{t('warehouse.assignTemplateForFiles')}</Typography>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 1 }}>
                <TextField label={t('personnel.fields.newCategory')} size="small" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} />
                <Button variant="contained" onClick={handleCreateCategory}>{t('common.create')}</Button>
              </Stack>
              {(machineFiles || []).map((entry) => (
                <Box key={entry.category.id} sx={{ mb: 2, border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
                    {renamingCategoryId === entry.category.id ? (
                      <>
                        <TextField size="small" value={renameCategoryName} onChange={(e) => setRenameCategoryName(e.target.value)} />
                        <Button size="small" onClick={() => handleRenameCategory(entry.category.id)}>{t('common.save')}</Button>
                        <Button size="small" onClick={() => { setRenamingCategoryId(null); setRenameCategoryName(''); }}>{t('common.cancel')}</Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle1">{entry.category.name}</Typography>
                        <Button size="small" onClick={() => { setRenamingCategoryId(entry.category.id); setRenameCategoryName(entry.category.name); }}>{t('common.rename')}</Button>
                        <Button size="small" color="error" onClick={() => handleDeleteCategory(entry.category.id)}>{t('common.delete')}</Button>
                      </>
                    )}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1 }}>
                    <DateInputWithArrows label={t('common.dueDate')} value={expirationByCategory[entry.category.id] || ''} onChange={(e) => setExpirationByCategory((prev) => ({ ...prev, [entry.category.id]: e.target.value }))} />
                    <TextField
                      size="small"
                      label={t('common.notes')}
                      value={notesByCategory[entry.category.id] || ''}
                      onChange={(e) => setNotesByCategory((prev) => ({ ...prev, [entry.category.id]: e.target.value }))}
                    />
                    <Button variant="outlined" component="label" size="small">
                      {t('personnel.files.uploadFile')}
                      <input hidden type="file" onChange={(e) => { const selectedFile = e.target.files?.[0]; if (selectedFile) handleUploadMachineFile(entry.category.id, selectedFile); e.target.value = ''; }} />
                    </Button>
                  </Stack>
                  {(entry.files || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('warehouse.noFilesInCategory')}</Typography>
                  ) : (
                    <List dense>
                      {entry.files.map((file) => (
                        <ListItem
                          key={file.id}
                          alignItems="flex-start"
                          sx={{ flexDirection: 'column', alignItems: 'stretch', px: 0 }}
                        >
                          <ListItemText
                            primary={file.originalName}
                            secondary={`${t('personnel.files.uploadedAt')}: ${new Date(file.uploadedAt).toLocaleString(i18n.language)}${file.expirationDate ? ` | ${t('personnel.files.expires')}: ${formatDateEuropean(file.expirationDate)}` : ''}${file.notes ? ` | ${file.notes}` : ''}`}
                          />
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            <Button size="small" href={file.url} target="_blank" rel="noreferrer">{t('common.open')}</Button>
                            <Button size="small" onClick={() => setDueDateDialog({ open: true, file })}>{t('common.updateDueDate')}</Button>
                            <Button size="small" onClick={() => setNotesDialog({ open: true, file })}>{t('common.updateNotes')}</Button>
                            <Button size="small" color="error" onClick={() => handleDeleteMachineFile(file.id)}>{t('common.delete')}</Button>
                          </Stack>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFiles}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
      <UpdateFileDueDateDialog
        open={dueDateDialog.open}
        onClose={closeDueDateDialog}
        currentExpirationDate={dueDateDialog.file?.expirationDate}
        onSave={handleSaveDueDate}
        saving={savingDueDate}
      />
      <UpdateFileNotesDialog
        open={notesDialog.open}
        onClose={closeNotesDialog}
        currentNotes={notesDialog.file?.notes}
        onSave={handleSaveNotes}
        saving={savingNotes}
      />

      <Dialog open={historyOpen} onClose={closeHistory} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle>{historyItem ? `${historyItem.name} ${t('materials.movementHistory', { defaultValue: 'movement history' })}` : t('materials.movementHistory', { defaultValue: 'Movement history' })}</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Resulting Qty</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Attachment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDateEuropean(row.movementDate)}</TableCell>
                    <TableCell>{getMovementLabel(row)}</TableCell>
                    <TableCell>{row.movementType === 'OUT' ? '-' : '+'}{row.quantity}</TableCell>
                    <TableCell>{row.resultingQuantity}</TableCell>
                    <TableCell>{row.project?.name || ''}</TableCell>
                    <TableCell>{row.notes || ''}</TableCell>
                    <TableCell>
                      {row.attachment?.url ? (
                        <Button size="small" onClick={() => window.open(row.attachment.url, '_blank', 'noopener,noreferrer')}>Open</Button>
                      ) : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHistory}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Materials;
